import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient
} from "@tanstack/react-query";
import {
	createFreelancer,
	getCurrentFreelancer,
	getFreelancer,
	patchFreelancer,
	searchFreelancers,
	updateFreelancer
} from "../api/freelancers-api";
import { useLanguage } from "../i18n/LanguageContext";
import { Freelancer } from "../models/Freelancer";

/**
 * Hook to get a specific freelancer with language support
 */
export const useFreelancer = (id: number, freelancerKey: string = "") => {
	const { currentLanguage } = useLanguage();

	return useQuery({
		queryKey: ["freelancer", id, currentLanguage],
		queryFn: () => getFreelancer(id, freelancerKey),
		enabled: id > 0 || (id <= 0 && freelancerKey !== ""),
		// currentLanguage is part of the key: on a language change the query refetches.
		// Keep the previous freelancer instead of going through `undefined`, otherwise the
		// derived profile id falls back to 0 and the profile screen briefly loses its data.
		placeholderData: keepPreviousData
	});
};

/**
 * Hook to search freelancers by email
 */
export const useSearchFreelancers = (email: string) => {
	return useQuery({
		queryKey: ["freelancers", email],
		queryFn: () => searchFreelancers(email)
	});
};

/**
 * Hook to create a new freelancer
 */
export const useCreateFreelancer = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (freelancer: Freelancer) => createFreelancer(freelancer),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["freelancers"] });
		}
	});
};

/**
 * Hook to update a freelancer with language support (PUT)
 */
export const useUpdateFreelancer = () => {
	const queryClient = useQueryClient();
	const { currentLanguage } = useLanguage();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: Freelancer }) =>
			updateFreelancer(id, data),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ["freelancer", id, currentLanguage]
			});
		}
	});
};

/**
 * Hook to patch a freelancer using JSON Patch operations with language support
 */
export const usePatchFreelancer = () => {
	const queryClient = useQueryClient();
	const { currentLanguage } = useLanguage();

	return useMutation({
		mutationFn: ({ id, operations }: { id: number; operations: any[] }) =>
			patchFreelancer(id, operations),
		onSuccess: updatedFreelancer => {
			// Invalidate both the specific freelancer and the current freelancer queries
			queryClient.invalidateQueries({
				queryKey: ["freelancer", updatedFreelancer.id, currentLanguage]
			});
			queryClient.invalidateQueries({
				queryKey: ["currentFreelancer", currentLanguage]
			});
		}
	});
};

/**
 * Hook to get the current user's freelancer profile with language support
 * @param enabled - Whether to enable the query (default: true)
 */
export const useCurrentFreelancer = (enabled: boolean = true) => {
	return useQuery({
		queryKey: ["currentFreelancer"],
		queryFn: () => getCurrentFreelancer(),
		enabled
	});
};
